import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/registration.css';
import '../../styles/auth-signup.css';
import { useLocale } from '../../context';
import RegistrationForm from './RegistrationForm';
import RegistrationLeftPanel from './RegistrationLeftPanel';
import LegalDocumentModal from './LegalDocumentModal';
import ConfirmCloseRegisterModal, {
  hasConfirmedCloseRegistration,
  isConfirmCloseEnabled,
  markConfirmedCloseRegistration,
} from './ConfirmCloseRegisterModal';
import { getLegalPage } from './legalPagesContent';
import { useSiteContent } from '../../context/SiteContentContext';
import { lockScroll, unlockScroll } from '../../scrollLock';
import { useRegisterModalFit } from '../../hooks/useRegisterModalFit';

function pickText(text, locale, key, fallback) {
  if (!text) return fallback;
  const enKey = `${key}En`;
  if (locale === 'en') return text[enKey] || text[key] || fallback;
  return text[key] || text[enKey] || fallback;
}

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M.456.454a1.094 1.094 0 0 1 1.548 0L9 7.468 16 .454A1.094 1.094 0 0 1 17.546 2l-7.014 6.998 7.015 6.998a1.087 1.087 0 0 1 0 1.545l-.002.003a1.09 1.09 0 0 1-1.545 0l-6.999-7.016-6.998 7.015v.001a1.09 1.09 0 0 1-1.545 0l-.003-.003a1.087 1.087 0 0 1 0-1.544l.001-.001 7.015-6.998L.456 2a1.094 1.094 0 0 1 0-1.547"
      />
    </svg>
  );
}

export default function RegistrationModal({ onClose, onLogin }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { register } = useSiteContent();
  const [legalPageId, setLegalPageId] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const legalPage = getLegalPage(legalPageId, locale);
  const formTitle = pickText(register?.text, locale, 'formTitle', tr('auth.register.title'));
  const haveAccount = pickText(register?.text, locale, 'haveAccount', tr('auth.haveAccount'));
  const loginLabel = pickText(register?.text, locale, 'login', tr('auth.login.link'));
  const { shellRef, fit } = useRegisterModalFit(true);

  const leaveRegister = useCallback(() => {
    markConfirmedCloseRegistration();
    setConfirmClose(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (!isConfirmCloseEnabled(register) || hasConfirmedCloseRegistration()) {
      leaveRegister();
      return;
    }
    setConfirmClose(true);
  }, [leaveRegister, register]);

  useEffect(() => {
    lockScroll();
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmClose) return;
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [confirmClose, requestClose]);

  const goLogin = () => {
    onClose();
    onLogin?.();
  };

  return createPortal(
    <>
      <div id="sign-up" className="modal-register modal-register--entered modal-register--fit">
        <div className="modal-register__backdrop" onClick={requestClose} style={{ transitionDuration: '300ms' }} />
        <div
          ref={shellRef}
          className="modal-register__content-wrapper"
          style={{
            width: fit.shellW,
            height: fit.shellH,
          }}
        >
          <div
            className="modal-register__content"
            role="dialog"
            aria-modal="true"
            aria-label={formTitle}
            style={{
              width: fit.w,
              height: fit.h,
              transform: `scale(${fit.scale})`,
              transformOrigin: 'top left',
            }}
          >
            <button
              type="button"
              id="modal-register-close-button"
              className="modal-register__close-button"
              onClick={requestClose}
              aria-label={tr('common.close')}
            >
              <IconClose />
            </button>
            <div className="auth auth--sign-up-modal">
              <RegistrationLeftPanel />
              <div className="auth__form-wrapper">
                <div className="auth__form-title">{formTitle}</div>
                <RegistrationForm
                  hideLoginLink
                  onSuccess={onClose}
                  onLoginClick={goLogin}
                  onOpenLegal={setLegalPageId}
                />
                <div className="auth__footer">
                  <div className="auth__link-wrapper">
                    <span>{haveAccount}</span>
                    <button type="button" className="auth__link" onClick={goLogin}>
                      {loginLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <LegalDocumentModal
          open={Boolean(legalPage)}
          title={legalPage?.title}
          html={legalPage?.html}
          onClose={() => setLegalPageId(null)}
        />
      </div>
      <ConfirmCloseRegisterModal
        open={confirmClose}
        onStay={() => setConfirmClose(false)}
        onLeave={leaveRegister}
      />
    </>,
    document.body,
  );
}
